import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { addDays } from 'date-fns';
import { WEEKDAYS_SHORT, dayKey, isSameDay, parseDayKey, weekDayLabel, weekOf } from './agendaDay';

// O seletor de data do iOS dispara uma mudança a cada giro da roda: aplica 400 ms depois da última
const DATE_DELAY = 400;

const countText = (count) => (count ? `${count} ${count === 1 ? 'agendamento' : 'agendamentos'}` : 'nenhum agendamento');

// Barra fixa da Lista: ‹, a data nativa, › e Hoje; embaixo, a semana (dom–sáb) com o número de agendamentos de cada dia.
// counts: dia ('yyyy-MM-dd') -> número; dia sem número (ainda carregando) fica em branco.
// mode 'week' (aba Semana): ‹ e › andam 7 dias e a faixa da semana sai (o cabeçalho da grade faz esse papel).
// onSelect(date, source): source 'step' (‹ ›), 'date' (o campo) ou 'week' (a faixa)
const DayNavigator = ({ day, today, counts, onSelect, onToday, barRef, mode = 'day' }) => {
  const week = mode === 'week';
  const step = week ? 7 : 1;
  const [draft, setDraft] = useState(() => dayKey(day));
  const timer = useRef(null);
  const latest = useRef({ day, onSelect });
  latest.current = { day, onSelect };

  useEffect(() => {
    clearTimeout(timer.current);
    setDraft(dayKey(day));
  }, [day]);

  useEffect(() => () => clearTimeout(timer.current), []);

  // Só valor válido e diferente do dia da tela; valor incompleto volta para o dia da tela ao sair do campo
  function apply(value, leaving = false) {
    clearTimeout(timer.current);
    const date = parseDayKey(value);
    if (date && value !== dayKey(latest.current.day)) latest.current.onSelect(date, 'date');
    else if (!date && leaving) setDraft(dayKey(latest.current.day));
  }

  function change(event) {
    const { value } = event.target;
    setDraft(value);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => apply(value), DATE_DELAY);
  }

  return (
    <div className='agenda-day-bar' ref={barRef}>
      <div className='agenda-day-row'>
        <Button type='button' variant='light' className='agenda-day-step' aria-label={week ? 'Semana anterior' : 'Dia anterior'} onClick={() => onSelect(addDays(day, -step), 'step')}>
          <IoChevronBack aria-hidden='true' />
        </Button>
        <input
          type='date'
          className='form-control agenda-day-date'
          aria-label='Escolher o dia'
          value={draft}
          onChange={change}
          onBlur={(event) => apply(event.target.value, true)}
        />
        <Button type='button' variant='light' className='agenda-day-step' aria-label={week ? 'Próxima semana' : 'Próximo dia'} onClick={() => onSelect(addDays(day, step), 'step')}>
          <IoChevronForward aria-hidden='true' />
        </Button>
        <Button type='button' variant='outline-dark' className='agenda-day-today' onClick={onToday}>Hoje</Button>
      </div>

      {week ? '' : (
        <div className='agenda-day-week' role='group' aria-label='Dias da semana'>
          {weekOf(day).map((date) => {
            const key = dayKey(date);
            const count = counts.get(key);
            const isToday = isSameDay(date, today);
            const label = `${weekDayLabel(date)}${count === undefined ? '' : `, ${countText(count)}`}${isToday ? ', hoje' : ''}`;

            return (
              <button
                key={key}
                type='button'
                aria-pressed={isSameDay(date, day)}
                aria-current={isToday ? 'date' : undefined}
                aria-label={label}
                onClick={() => onSelect(date, 'week')}
              >
                <span aria-hidden='true'>{WEEKDAYS_SHORT[date.getDay()]}</span>
                <span className='agenda-day-week-day' aria-hidden='true'>{date.getDate()}</span>
                <span className='agenda-day-week-count' aria-hidden='true'>{count === undefined ? '' : (count || '–')}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DayNavigator;
